import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Select,
  Space,
  Typography,
  Tag,
  Button,
  Row,
  Col,
  DatePicker,
  Input,
  Tooltip,
  Switch,
  Badge,
  Drawer,
  message,
  Alert,
  Popconfirm,
} from 'antd';
import {
  ReloadOutlined,
  DownloadOutlined,
  ClearOutlined,
  EyeOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { apiService } from '../services/api';
import { App as ApiApp } from '../types/api';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import TableSkeleton from '../components/ui/TableSkeleton';
import { AppStatus } from '../types/enums';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

interface LogEntry {
  id: string;
  timestamp: string;
  level: 'info' | 'warn' | 'error' | 'debug';
  message: string;
  appId: string;
  appName: string;
  source: string;
  requestId?: string;
  duration?: number;
  statusCode?: number;
  userAgent?: string;
  ip?: string;
  method?: string;
  url?: string;
  data?: any;
  workerName?: string;
  eventType?: string;
  outcome?: string;
}

interface App {
  id: string;
  name: string;
  domain: string;
  status: AppStatus | 'deploying' | 'error' | string; // 'deploying' та 'error' - legacy статуси
}

export const Logs: React.FC = () => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [apps, setApps] = useState<App[]>([]);
  const [selectedApp, setSelectedApp] = useState<string>('all');
  const [selectedLevel, setSelectedLevel] = useState<string>('all');
  const [searchText, setSearchText] = useState('');
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshIntervalDuration, setRefreshIntervalDuration] = useState(5000); // 5 секунд за замовчуванням
  const [lastRefreshTime, setLastRefreshTime] = useState<string | null>(null);
  const [lastLogTimestamp, setLastLogTimestamp] = useState<string | null>(null);
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const [selectedLogEntry, setSelectedLogEntry] = useState<LogEntry | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState<NodeJS.Timeout | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [pageSize, setPageSize] = useState(50);
  const { t } = useTranslation();
  const { isDark } = useTheme();

  // Load real data from API
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        
        // Load apps
        const { apps: appsList } = await apiService.getApps();
        setApps(appsList.map((app: ApiApp) => ({
          id: app.id,
          name: app.name,
          domain: app.domain || '',
          status: app.status as AppStatus | 'deploying' | 'error' | string
        })));

        // Load logs for first app if exists
        if (appsList.length > 0) {
          const logsData = await apiService.getAppLogs(appsList[0].id, 1000);
          setLogs(logsData || []);
        }

      } catch (error) {
        console.error('Помилка завантаження даних:', error);
        message.error('Не вдалося завантажити дані');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  // Load logs when selectedApp changes
  useEffect(() => {
    const loadLogsForApp = async () => {
      if (selectedApp && selectedApp !== 'all') {
        try {
          setIsLoading(true);
          const logsData = await apiService.getAppLogs(selectedApp, 1000);
          setLogs(logsData || []);
        } catch (error) {
          console.error('Помилка завантаження логів:', error);
          message.error(`Не вдалося завантажити логи: ${error instanceof Error ? error.message : 'Невідома помилка'}`);
        } finally {
          setIsLoading(false);
        }
      } else if (selectedApp === 'all') {
        // Завантажуємо логи для всіх застосунків
        try {
          setIsLoading(true);
          const allLogsPromises = apps.map(app => apiService.getAppLogs(app.id, 1000));
          const allLogsData = await Promise.all(allLogsPromises);
          const combinedLogs = allLogsData.flat().filter(Boolean);
          // Сортуємо за часом (найновіші спочатку)
          combinedLogs.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
          setLogs(combinedLogs);
        } catch (error) {
          console.error('Помилка завантаження логів для всіх застосунків:', error);
          message.error(`Не вдалося завантажити логи: ${error instanceof Error ? error.message : 'Невідома помилка'}`);
        } finally {
          setIsLoading(false);
        }
      } else {
        setLogs([]);
      }
    };

    if (apps.length > 0) {
      loadLogsForApp();
    }
  }, [selectedApp, apps]);

  // Auto-refresh functionality
  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(() => {
        refreshLogs(true);
      }, refreshIntervalDuration);
      setRefreshInterval(interval);
    } else {
      if (refreshInterval) {
        clearInterval(refreshInterval);
        setRefreshInterval(null);
      }
    }
    
    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, [autoRefresh, refreshIntervalDuration]);

  const refreshLogs = async (isAutoRefresh = false) => {
    try {
      setIsRefreshing(true);
      
      if (selectedApp === 'all') {
        // Оновлюємо логи для всіх застосунків
        const allLogsPromises = apps.map(app => 
          apiService.getAppLogs(app.id, 1000, isAutoRefresh && lastLogTimestamp ? lastLogTimestamp : undefined)
        );
        const allLogsData = await Promise.all(allLogsPromises);
        const combinedLogs = allLogsData.flat().filter(Boolean);
        combinedLogs.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
        
        if (isAutoRefresh && lastLogTimestamp) {
          // Додаємо нові логи до існуючих
          const newLogs = [...combinedLogs, ...logs];
          // Видаляємо дублікати та сортуємо
          const uniqueLogs = newLogs.filter((log, index, self) => 
            index === self.findIndex(l => l.id === log.id)
          ).sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
          setLogs(uniqueLogs);
        } else {
          setLogs(combinedLogs);
        }
      } else if (selectedApp) {
        // Оновлюємо логи для конкретного застосунку
        const logsData = await apiService.getAppLogs(
          selectedApp, 
          1000, 
          isAutoRefresh && lastLogTimestamp ? lastLogTimestamp : undefined
        );
        
        if (isAutoRefresh && lastLogTimestamp) {
          // Додаємо нові логи до існуючих
          const newLogs = [...(logsData || []), ...logs];
          // Видаляємо дублікати та сортуємо
          const uniqueLogs = newLogs.filter((log, index, self) => 
            index === self.findIndex(l => l.id === log.id)
          ).sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
          setLogs(uniqueLogs);
        } else {
          setLogs(logsData || []);
        }
      } else {
        // Якщо нічого не вибрано, просто очищуємо
        setLogs([]);
      }
      
      // Оновлюємо timestamp останнього лога
      if (logs.length > 0) {
        setLastLogTimestamp(logs[0].timestamp);
      }
      
      setLastRefreshTime(new Date().toLocaleString());
      
      if (!isAutoRefresh) {
        message.success(t('logs.refresh'));
      }
    } catch (error) {
      console.error('Помилка оновлення логів:', error);
      message.error(`Не вдалося оновити логи: ${error instanceof Error ? error.message : 'Невідома помилка'}`);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleRefresh = async () => {
    await refreshLogs(false);
  };

  const handleViewDetails = (log: LogEntry) => {
    setSelectedLogEntry(log);
    setIsDrawerVisible(true);
  };

  const handleExport = () => {
    const filteredLogs = getFilteredLogs();
    const csvContent = [
      ['Timestamp', 'Level', 'App', 'Message', 'Status', 'Duration', 'Request ID'].join(','),
      ...filteredLogs.map(log => [
        log.timestamp,
        log.level,
        log.appName,
        `"${log.message}"`,
        log.statusCode || '',
        log.duration || '',
        log.requestId || ''
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = `worker-logs-${dayjs().format('YYYY-MM-DD')}.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    message.success('Логи експортовано');
  };

  const handleClearLogs = () => {
    // TODO: Implement API call to clear logs
    // await apiService.clearAppLogs(selectedApp);
    setLogs([]);
    message.success('Логи очищено локально');
  };

  const handleDeleteSelected = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Виберіть логи для видалення');
      return;
    }

    try {
      // Тимчасово видаляємо тільки локально, поки backend не підтримує DELETE /logs
      // TODO: Додати backend API endpoint для видалення логів
      const filteredLogs = logs.filter(log => !selectedRowKeys.includes(log.id));
      setLogs(filteredLogs);
      setSelectedRowKeys([]);
      message.success(`Видалено ${selectedRowKeys.length} записів локально`);
    } catch (error) {
      console.error('Помилка видалення логів:', error);
      message.error('Помилка при видаленні логів');
    }
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys: React.Key[]) => {
      setSelectedRowKeys(selectedKeys);
    },
    onSelectAll: (selected: boolean, _selectedRows: LogEntry[], _changeRows: LogEntry[]) => {
      if (selected) {
        const allKeys = getFilteredLogs().map(log => log.id);
        setSelectedRowKeys(allKeys);
      } else {
        setSelectedRowKeys([]);
      }
    },
  };

  const getFilteredLogs = () => {
    if (!Array.isArray(logs)) {
      return [];
    }
    
    return logs.filter(log => {
      const matchesApp = selectedApp === 'all' || log.appId === selectedApp;
      const matchesLevel = selectedLevel === 'all' || log.level === selectedLevel;
      const matchesSearch = searchText === '' || 
        log.message.toLowerCase().includes(searchText.toLowerCase()) ||
        (log.appName && log.appName.toLowerCase().includes(searchText.toLowerCase())) ||
        (log.requestId && log.requestId.toLowerCase().includes(searchText.toLowerCase()));
      
      let matchesDate = true;
      if (dateRange) {
        const logDate = dayjs(log.timestamp);
        matchesDate = logDate.isAfter(dateRange[0]) && logDate.isBefore(dateRange[1]);
      }
      
      return matchesApp && matchesLevel && matchesSearch && matchesDate;
    });
  };

  const getLevelIcon = (level: LogEntry['level']) => {
    switch (level) {
      case 'info': return <CheckCircleOutlined />;
      case 'warn': return <ExclamationCircleOutlined />;
      case 'error': return <CloseCircleOutlined />;
      case 'debug': return <EyeOutlined />;
      default: return <ClockCircleOutlined />;
    }
  };

  const getLevelColor = (level: LogEntry['level']) => {
    switch (level) {
      case 'info': return 'success';
      case 'warn': return 'warning';
      case 'error': return 'error';
      case 'debug': return 'default';
      default: return 'default';
    }
  };

  const getStatusColor = (statusCode?: number) => {
    if (!statusCode) return 'default';
    if (statusCode >= 200 && statusCode < 300) return 'success';
    if (statusCode >= 300 && statusCode < 400) return 'processing';
    if (statusCode >= 400 && statusCode < 500) return 'warning';
    if (statusCode >= 500) return 'error';
    return 'default';
  };

  const columns: ColumnsType<LogEntry> = [
    {
      title: t('logs.timestamp'),
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 180,
      render: (timestamp: string) => (
        <Text style={{ fontFamily: 'monospace' }}>
          {dayjs(timestamp).format('DD/MM HH:mm:ss')}
        </Text>
      ),
    },
    {
      title: t('logs.level'),
      dataIndex: 'level',
      key: 'level',
      width: 100,
      render: (level: LogEntry['level']) => (
        <Tag color={getLevelColor(level)} icon={getLevelIcon(level)}>
          {level.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: t('logs.app'),
      dataIndex: 'appName',
      key: 'appName',
      width: 200,
      render: (appName: string, record: LogEntry) => (
        <Space direction="vertical" size="small">
          <Text strong>{appName}</Text>
          <Tag color={record.source === 'cloudflare' ? 'blue' : 'green'} style={{ fontSize: '10px' }}>
            {record.source === 'cloudflare' ? 'CF' : 'DB'}
          </Tag>
        </Space>
      ),
    },
    {
      title: t('logs.message'),
      dataIndex: 'message',
      key: 'message',
      ellipsis: true,
      render: (message: string) => (
        <Text style={{ fontFamily: 'monospace', fontSize: 12 }}>
          {message}
        </Text>
      ),
    },
    {
      title: t('ui.table.status'),
      dataIndex: 'statusCode',
      key: 'statusCode',
      width: 100,
      render: (statusCode: number) => 
        statusCode ? (
          <Badge status={getStatusColor(statusCode)} text={statusCode.toString()} />
        ) : null,
    },
    {
      title: 'Request ID',
      dataIndex: 'requestId',
      key: 'requestId',
      width: 150,
      render: (requestId: string) => 
        requestId ? (
          <Text code style={{ fontSize: 11 }}>
            {requestId}
          </Text>
        ) : null,
    },
    {
      title: t('logs.actions'),
      key: 'actions',
      width: 80,
      render: (_, record: LogEntry) => (
        <Tooltip title={t('logs.detailsTooltip')}>
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetails(record)}
          />
        </Tooltip>
      ),
    },
  ];

  const filteredLogs = getFilteredLogs();

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={2}>{t('logs.title')}</Title>
        <Space>
          <Switch
            checked={autoRefresh}
            onChange={setAutoRefresh}
            checkedChildren={t('logs.auto')}
            unCheckedChildren={t('logs.manual')}
          />
          {autoRefresh && (
            <Select
              value={refreshIntervalDuration}
              onChange={setRefreshIntervalDuration}
              style={{ width: 120 }}
              size="small"
            >
              <Select.Option value={1000}>{t('logs.autoRefreshIntervals.1')}</Select.Option>
              <Select.Option value={2000}>{t('logs.autoRefreshIntervals.2')}</Select.Option>
              <Select.Option value={5000}>{t('logs.autoRefreshIntervals.5')}</Select.Option>
              <Select.Option value={10000}>{t('logs.autoRefreshIntervals.10')}</Select.Option>
              <Select.Option value={30000}>{t('logs.autoRefreshIntervals.30')}</Select.Option>
              <Select.Option value={60000}>{t('logs.autoRefreshIntervals.60')}</Select.Option>
            </Select>
          )}
          <Button
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            loading={isRefreshing}
            size="small"
          >
            {t('logs.refresh')}
          </Button>
        </Space>
      </div>

      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]}>
          <Col span={5}>
            <Select
              value={selectedApp}
              onChange={setSelectedApp}
              style={{ width: '100%' }}
              placeholder={t('logs.allApps')}
            >
              <Option value="all">{t('logs.allApps')}</Option>
              {apps.map(app => (
                <Option key={app.id} value={app.id}>
                  <Space>
                    <Badge status={app.status === 'active' ? 'success' : 'default'} />
                    {app.name}
                  </Space>
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={4}>
            <Select
              value={selectedLevel}
              onChange={setSelectedLevel}
              style={{ width: '100%' }}
              placeholder={t('logs.allLevels')}
            >
              <Option value="all">{t('logs.allLevels')}</Option>
              <Option value="info">{t('logs.info')}</Option>
              <Option value="warn">{t('logs.warn')}</Option>
              <Option value="error">{t('logs.error')}</Option>
              <Option value="debug">{t('logs.debug')}</Option>
            </Select>
          </Col>
          <Col span={6}>
                         <RangePicker
               value={dateRange}
               onChange={(dates) => {
                 if (dates && dates[0] && dates[1]) {
                   setDateRange([dates[0], dates[1]]);
                 } else {
                   setDateRange(null);
                 }
               }}
               style={{ width: '100%' }}
               placeholder={[t('logs.dateRange.today'), t('logs.dateRange.yesterday')]}
               showTime={{
                 format: 'HH:mm:ss',
               }}
               format="YYYY-MM-DD HH:mm:ss"
             />
          </Col>
          <Col span={5}>
            <Input.Search
              placeholder={t('logs.search')}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: '100%' }}
            />
          </Col>
          <Col span={4}>
            <Space>
              <Tooltip title={t('logs.exportTooltip')}>
                <Button
                  icon={<DownloadOutlined />}
                  onClick={handleExport}
                  disabled={filteredLogs.length === 0}
                />
              </Tooltip>
              <Tooltip title={t('logs.clearTooltip')}>
                <Button
                  icon={<ClearOutlined />}
                  onClick={handleClearLogs}
                  disabled={logs.length === 0}
                />
              </Tooltip>
              <Popconfirm
                title={t('logs.clearSelectedConfirm')}
                description={t('logs.deleteSelectedConfirm', { count: selectedRowKeys.length })}
                onConfirm={handleDeleteSelected}
                okText={t('logs.yes')}
                cancelText={t('logs.no')}
              >
                <Tooltip title={t('logs.clearSelectedTooltip')}>
                  <Button
                    icon={<DeleteOutlined />}
                    danger
                    disabled={selectedRowKeys.length === 0}
                  />
                </Tooltip>
              </Popconfirm>
            </Space>
          </Col>
        </Row>
      </Card>

      {autoRefresh && (
        <Alert
          message={t('logs.autoRefreshEnabled')}
          description={t('logs.refreshing', { seconds: refreshIntervalDuration / 1000 }) + (lastRefreshTime ? ` • ${t('logs.lastCheck', { time: lastRefreshTime })}` : '')}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Card>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space>
            <Text type="secondary">
              {t('logs.showingRecords', { from: filteredLogs.length, total: logs.length })}
            </Text>
            {lastRefreshTime && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                • {t('logs.updated')}: {lastRefreshTime}
              </Text>
            )}
            {selectedRowKeys.length > 0 && (
              <Tag color="blue">
                {t('logs.selected', { count: selectedRowKeys.length })}
              </Tag>
            )}
          </Space>
          <Space>
            <Badge count={filteredLogs.filter(l => l.level === 'error').length} status="error" />
            <Badge count={filteredLogs.filter(l => l.level === 'warn').length} status="warning" />
            <Badge count={filteredLogs.filter(l => l.level === 'info').length} status="success" />
          </Space>
        </div>

        {isLoading ? (
          <TableSkeleton 
            rows={pageSize} 
            columns={columns}
            showHeader={true}
            showPagination={true}
            animated={true}
          />
        ) : (
        <Table
          dataSource={filteredLogs}
          columns={columns}
          rowKey="id"
          rowSelection={rowSelection}
          pagination={{
            pageSize: pageSize,
            showSizeChanger: true,
            showQuickJumper: true,
              showTotal: (total, range) => t('logs.paginationRange', { from: `${range[0]}-${range[1]}`, total }),
            pageSizeOptions: ['100', '200', '500', '1000', '2000', '5000', '10000'],
            onShowSizeChange: (_current, size) => {
              setPageSize(size);
            },
          }}
          size="small"
          scroll={{ x: true }}
        />
        )}
      </Card>

      <Drawer
        title={t('logs.logDetailsTitle')}
        open={isDrawerVisible}
        onClose={() => setIsDrawerVisible(false)}
        width={600}
      >
        {selectedLogEntry && (
          <div>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Card size="small" title={t('logs.basicInfo')}>
                  <Row gutter={[16, 8]}>
                    <Col span={8}>
                      <Text strong>{t('logs.timestamp')}:</Text>
                    </Col>
                    <Col span={16}>
                      <Text code>{dayjs(selectedLogEntry.timestamp).format('DD/MM/YYYY HH:mm:ss')}</Text>
                    </Col>
                    <Col span={8}>
                      <Text strong>{t('logs.level')}:</Text>
                    </Col>
                    <Col span={16}>
                      <Tag color={getLevelColor(selectedLogEntry.level)} icon={getLevelIcon(selectedLogEntry.level)}>
                        {selectedLogEntry.level.toUpperCase()}
                      </Tag>
                    </Col>
                    <Col span={8}>
                      <Text strong>{t('logs.app')}:</Text>
                    </Col>
                    <Col span={16}>
                      <Text>{selectedLogEntry.appName}</Text>
                    </Col>
                    <Col span={8}>
                      <Text strong>{t('logs.source')}:</Text>
                    </Col>
                    <Col span={16}>
                      <Text>{selectedLogEntry.source}</Text>
                    </Col>
                  </Row>
                </Card>
              </Col>

              <Col span={24}>
                <Card size="small" title={t('logs.messageCard')}>
                  <div style={{ 
                    background: isDark ? '#262626' : '#f5f5f5', 
                    padding: '12px', 
                    borderRadius: '6px',
                    fontFamily: 'monospace',
                    fontSize: '13px',
                    lineHeight: '1.4',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                  }}>
                    {selectedLogEntry.message}
                  </div>
                </Card>
              </Col>

              {(selectedLogEntry.method || selectedLogEntry.url || selectedLogEntry.userAgent || selectedLogEntry.ip) && (
                <Col span={24}>
                  <Card size="small" title={t('logs.httpDetails')}>
                    <Row gutter={[16, 8]}>
                      {selectedLogEntry.method && (
                        <>
                          <Col span={8}>
                            <Text strong>{t('logs.method')}:</Text>
                          </Col>
                          <Col span={16}>
                            <Tag color="blue">{selectedLogEntry.method}</Tag>
                          </Col>
                        </>
                      )}
                      {selectedLogEntry.url && (
                        <>
                          <Col span={8}>
                            <Text strong>{t('logs.url')}:</Text>
                          </Col>
                          <Col span={16}>
                            <Text code style={{ fontSize: '12px' }}>{selectedLogEntry.url}</Text>
                          </Col>
                        </>
                      )}
                      {selectedLogEntry.userAgent && (
                        <>
                          <Col span={8}>
                            <Text strong>{t('logs.userAgent')}:</Text>
                          </Col>
                          <Col span={16}>
                            <Text style={{ fontSize: '12px' }}>{selectedLogEntry.userAgent}</Text>
                          </Col>
                        </>
                      )}
                      {selectedLogEntry.ip && (
                        <>
                          <Col span={8}>
                            <Text strong>{t('logs.ipAddress')}:</Text>
                          </Col>
                          <Col span={16}>
                            <Text code>{selectedLogEntry.ip}</Text>
                          </Col>
                        </>
                      )}
                    </Row>
                  </Card>
                </Col>
              )}

              {selectedLogEntry.data && (
                <Col span={24}>
                  <Card size="small" title={t('logs.additionalData')}>
                    <pre style={{ 
                      background: isDark ? '#262626' : '#f8f8f8', 
                      padding: '12px', 
                      borderRadius: '6px',
                      fontSize: '12px',
                      lineHeight: '1.4',
                      overflow: 'auto'
                    }}>
                      {JSON.stringify(selectedLogEntry.data, null, 2)}
                    </pre>
                  </Card>
                </Col>
              )}
            </Row>
          </div>
        )}
      </Drawer>
    </div>
  );
}; 